import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import {
  OPERATION_MFA_VIEW_TYPE,
  type OperationMFAStatus,
  type OperationMFAVerificationOptions,
  type OperationMFAViewState,
} from './types';
import { type WithdrawOperationHandledErrorResponse } from '@/shared/types';
import {
  MFA_IDENTITY_TYPES,
  type MFAIdentityType,
  type TwoFA,
  useTwoFactorAuthIdentities,
} from '@/entities/mfa-identity';
import { useTwoFactorSettingsModals } from '@/features/user-settings-2fa';
import { useSessionStore } from '@/entities/session';
import { useTranslation } from 'react-i18next';
import { isVerificationCodeInvalidError } from '@/shared/utils';
import { MFA_REQUIRED_RESPONSE_CODE } from '@/shared/config';

type MFAFormValues = {
  twoFA: TwoFA;
};

export const useOperationMFAVerification = <
  TParams = unknown,
  TResponse = unknown,
  TError extends AxiosError = AxiosError,
>({
  useMutationHook,
  initialParams,
  injectMFA,
  requireMFASetup = true,
  onSuccess,
  onError,
  onCancel,
  onBack,
  onClose,
  onMFASetupComplete,
}: OperationMFAVerificationOptions<TParams, TResponse, TError>) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { session } = useSessionStore();
  const { openTwoFactorAppEnableModal } = useTwoFactorSettingsModals();
  const {
    twoFactorAuthIdentities,
    isTwoFactorAuthIdentitiesLoading,
    twoFactorAuthIdentitiesError,
  } = useTwoFactorAuthIdentities();

  const [selectedIdentityType, setSelectedIdentityType] =
    useState<MFAIdentityType | null>(null);
  const [maskedContact, setMaskedContact] = useState<string>('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequiredData, setMfaRequiredData] = useState<
    WithdrawOperationHandledErrorResponse['data'] | null
  >(null);

  const form = useForm<MFAFormValues>({
    defaultValues: {
      twoFA: {
        state: '',
        code: '',
        identityType: MFA_IDENTITY_TYPES.GOOGLE_AUTHENTICATOR,
      },
    },
  });

  const enabledIdentities = twoFactorAuthIdentities
    ? twoFactorAuthIdentities.filter(i => i.isEnabled)
    : [];
  const hasMFAConfigured = enabledIdentities.length > 0;

  const handleError = useCallback(
    (err: TError) => {
      const axiosError =
        err as AxiosError<WithdrawOperationHandledErrorResponse>;

      if (isVerificationCodeInvalidError(axiosError?.response?.data)) {
        form.resetField('twoFA.code');
      }

      if (
        axiosError.response?.data?.code === MFA_REQUIRED_RESPONSE_CODE &&
        axiosError.response.data.data
      ) {
        setMfaRequiredData(axiosError.response.data.data);
        form.setValue('twoFA.state', axiosError.response.data.data.state);
      } else if (
        axiosError.response?.data?.code === 'VERIFICATION_CODE_INVALID'
      ) {
        setError(axiosError.response?.data.message || t('common.errorTitle'));
      } else {
        onError?.(err);
      }
    },
    [form, onError]
  );

  const mutation = useMutationHook({ onSuccess, onError: handleError });

  const sendInitialRequest = useCallback(() => {
    setError(null);
    mutation.mutate(initialParams);
  }, [mutation, initialParams]);

  const startRequestRef = useRef<(() => void) | null>(null);
  const pendingStartRequestRef = useRef(false);

  const handleSetupRequired = useCallback(() => {
    openTwoFactorAppEnableModal({
      onBack: () => {
        setMfaRequiredData(null);
        onBack?.();
      },
      onClose: () => {
        setMfaRequiredData(null);
        onClose?.();
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['user-profile-two-factor-settings', session?.userId],
        });

        await queryClient.refetchQueries({
          queryKey: ['user-profile-two-factor-settings', session?.userId],
        });

        setMfaRequiredData(null);
        setSelectedIdentityType(null);
        setError(null);
        form.reset();

        onMFASetupComplete?.();

        pendingStartRequestRef.current = true;
      },
      preventOverlayClose: true,
    });
  }, [
    openTwoFactorAppEnableModal,
    queryClient,
    session?.userId,
    onCancel,
    onBack,
    onClose,
    onMFASetupComplete,
    form,
    pendingStartRequestRef.current,
  ]);

  const startRequest = useCallback(() => {
    if (isTwoFactorAuthIdentitiesLoading) {
      pendingStartRequestRef.current = true; // Marked to retry
      return;
    }

    pendingStartRequestRef.current = false;

    const enabledIdentities = (twoFactorAuthIdentities || []).filter(
      i => i.isEnabled
    );

    if (requireMFASetup && enabledIdentities.length === 0) {
      handleSetupRequired();
      return;
    }

    sendInitialRequest();
  }, [
    isTwoFactorAuthIdentitiesLoading,
    twoFactorAuthIdentities,
    requireMFASetup,
    sendInitialRequest,
    handleSetupRequired,
  ]);

  startRequestRef.current = startRequest;

  useEffect(() => {
    if (!isTwoFactorAuthIdentitiesLoading && pendingStartRequestRef.current) {
      pendingStartRequestRef.current = false;
      startRequest();
    }
  }, [isTwoFactorAuthIdentitiesLoading, startRequest]);

  useEffect(() => {
    if (mfaRequiredData) {
      const enabled = mfaRequiredData.identityTypes.filter(i => i.isEnabled);

      if (enabled.length === 1) {
        const single = enabled[0];
        setSelectedIdentityType(single.name.toLowerCase() as MFAIdentityType);
        form.setValue('twoFA.identityType', single.name as MFAIdentityType);
      }
    }
  }, [mfaRequiredData, form]);

  const handleMethodSelected = useCallback(
    (identityType: MFAIdentityType) => {
      setSelectedIdentityType(identityType);
      form.setValue('twoFA.identityType', identityType);
    },
    [form]
  );

  const handleOTPSubmit = useCallback(() => {
    const mfa = form.getValues().twoFA;
    if (!mfa.code || !mfa.state || !mfa.identityType) return;

    setError(null);
    const paramsWithMFA = injectMFA(initialParams, {
      state: mfa.state,
      code: mfa.code,
      identityType: mfa.identityType,
    });
    mutation.mutate(paramsWithMFA);
  }, [form, initialParams, injectMFA, mutation]);

  const handleCancel = useCallback(() => {
    setMfaRequiredData(null);
    setSelectedIdentityType(null);
    form.reset();
    onCancel?.();
  }, [form, onCancel]);

  const viewState: OperationMFAViewState = useMemo(() => {
    if (isTwoFactorAuthIdentitiesLoading) {
      return OPERATION_MFA_VIEW_TYPE.CONFIRMATION;
    }

    if (requireMFASetup && !hasMFAConfigured) {
      return OPERATION_MFA_VIEW_TYPE.SETUP_REQUIRED;
    }

    if (!mfaRequiredData) {
      return OPERATION_MFA_VIEW_TYPE.CONFIRMATION;
    }

    const enabled = mfaRequiredData.identityTypes.filter(i => i.isEnabled);

    if (enabled.length > 1 && !selectedIdentityType) {
      return OPERATION_MFA_VIEW_TYPE.METHOD_SELECTION;
    }

    return OPERATION_MFA_VIEW_TYPE.CONFIRMATION;
  }, [
    isTwoFactorAuthIdentitiesLoading,
    hasMFAConfigured,
    requireMFASetup,
    mfaRequiredData,
    selectedIdentityType,
  ]);

  const status: OperationMFAStatus = useMemo(() => {
    const isLoading =
      isTwoFactorAuthIdentitiesLoading || mutation.isPending || isSendingOTP;
    const isSubmitting = mutation.isPending;
    const hasError = !!error || !!twoFactorAuthIdentitiesError;
    const errorMessage =
      error ||
      (twoFactorAuthIdentitiesError instanceof Error
        ? twoFactorAuthIdentitiesError.message
        : undefined);

    return {
      viewState,
      isLoading,
      isReady: !isLoading && !!mfaRequiredData,
      isSubmitting,
      hasError,
      errorMessage,
    };
  }, [
    isTwoFactorAuthIdentitiesLoading,
    mutation.isPending,
    mutation.error,
    isSendingOTP,
    error,
    twoFactorAuthIdentitiesError,
    viewState,
    mfaRequiredData,
  ]);

  const availableMethods = mfaRequiredData
    ? mfaRequiredData.identityTypes
        .filter(i => i.isEnabled)
        .map(i => ({
          name: i.name.toLowerCase() as MFAIdentityType,
          isEnabled: i.isEnabled,
        }))
    : [];

  const handleBackToMethodSelection = useCallback(() => {
    if (
      selectedIdentityType &&
      viewState === OPERATION_MFA_VIEW_TYPE.CONFIRMATION &&
      availableMethods.length > 1
    ) {
      setSelectedIdentityType(null);
      form.resetField('twoFA.code');
      form.resetField('twoFA.identityType');
      setMaskedContact('');
      return;
    }

    onBack?.();
  }, [viewState, availableMethods.length, selectedIdentityType, form, onBack]);

  return {
    form,
    status,
    viewState,
    selectedIdentityType,
    maskedContact,
    setMaskedContact,
    setIsSendingOTP,
    setError,
    handleSetupRequired,
    handleMethodSelected,
    handleOTPSubmit,
    handleCancel,
    startRequest,
    availableMethods,
    isTwoFactorAuthIdentitiesLoading,
    handleBackToMethodSelection,
  };
};
